/* ══════════════════════════════════════════════════════════════════════════════════════════════
   W63 — THE "ASSIGN A WORKOUT" PICKER'S RULES, COPIED FROM THEIRS (EVERFIT-MAP §8a-ter, re-read
   live on his account 2026-09-24).

   Their dialog, as read: a search by workout NAME that needs no submit and re-selects the first
   result; an overline that names the MODE as well as the count — `MOST RECENT (10)`, then
   `RESULTS (2)` while searching, then bare `RESULTS` over `No result found.`; rows reading
   `name · N Exercises · M Sections · age` with the sections half dropped when there are none;
   the first row chosen on arrival so the preview is never empty.

   Pure functions, so every one of those sentences is pinned by a test rather than by a still.
   ══════════════════════════════════════════════════════════════════════════════════════════════ */

import { daysSince } from "./days";

export type PickerRow = {
  id: string;
  name: string;
  exercises: number;
  sections: number;
  /** The last day this template was given to anybody — the library's own question. */
  lastUsedISO: string | null;
};

/** MOST RECENT first: the newest last-used day, then everything never assigned, each by name. */
export function mostRecentFirst<R extends PickerRow>(rows: readonly R[]): R[] {
  return [...rows].sort((a, b) => {
    if (a.lastUsedISO !== b.lastUsedISO) {
      if (a.lastUsedISO === null) return 1;
      if (b.lastUsedISO === null) return -1;
      return b.lastUsedISO.localeCompare(a.lastUsedISO);
    }
    return a.name.localeCompare(b.name);
  });
}

/** Their search matches the NAME alone, case-blind, live; an empty query is the whole list. */
export function searchByName<R extends PickerRow>(rows: readonly R[], query: string): R[] {
  const q = query.trim().toLowerCase();
  if (q === "") return [...rows];
  return rows.filter((r) => r.name.toLowerCase().includes(q));
}

/** The overline over the list — it names the MODE, and a search with no match loses its count. */
export function pickerOverline(query: string, shown: number): string {
  if (query.trim() === "") return `Most recent (${String(shown)})`;
  return shown === 0 ? "Results" : `Results (${String(shown)})`;
}

/** `20 Exercises · 9 Parts`, `4 Exercises` — the parts half only when there are some (W88: a section is a part). */
export function pickerSize(row: Pick<PickerRow, "exercises" | "sections">): string {
  const ex = `${String(row.exercises)} ${row.exercises === 1 ? "Exercise" : "Exercises"}`;
  if (row.sections <= 0) return ex;
  return `${ex} · ${String(row.sections)} ${row.sections === 1 ? "Section" : "Sections"}`;
}

/** Their age badge is compact (`3w`, `16w`). Ours answers the same question the library's
 *  `describeAge` answers — when it was last GIVEN — and says `new` for never, never a blank. */
export function ageBadge(lastUsedISO: string | null, today: Date): string {
  if (lastUsedISO === null) return "new";
  const d = daysSince(lastUsedISO, today);
  if (d === null) return "";
  // Assigned AHEAD (his week is written in advance, so this is most rows): the day it is next
  // given, `28 Sep`, is information; "ahead" on every row was not (measured on the first render).
  if (d < 0) {
    const [, m, day] = lastUsedISO.slice(0, 10).split("-");
    const month = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"][Number(m) - 1];
    return `${String(Number(day))} ${month ?? ""}`.trim();
  }
  if (d === 0) return "today";
  if (d < 7) return `${String(d)}d`;
  if (d < 365) return `${String(Math.floor(d / 7))}w`;
  return `${String(Math.floor(d / 365))}y`;
}

/** Which row is chosen after the list changes: the one already chosen if it is still shown,
 *  otherwise the first — theirs re-selects the first result as you type. */
export function keepOrFirst(shown: readonly PickerRow[], chosenId: string | null): string | null {
  if (chosenId !== null && shown.some((r) => r.id === chosenId)) return chosenId;
  return shown[0]?.id ?? null;
}

/** The address keys the picker lives at: `view=find-a-workout&date=YYYY-MM-DD` (theirs, verbatim). */
export const PICKER_VIEW = "find-a-workout";

/** The day the picker is for, or null when the address does not name a well-formed one. */
export function pickerDay(q: Record<string, string>): string | null {
  if (q["view"] !== PICKER_VIEW) return null;
  const d = q["date"] ?? "";
  return /^\d{4}-\d{2}-\d{2}$/.test(d) ? d : null;
}

/** The query with the picker opened on `day`, every other key (tab, span) kept. */
export function withPicker(q: Record<string, string>, day: string): string {
  const next = new URLSearchParams(q);
  next.set("view", PICKER_VIEW);
  next.set("date", day);
  return `?${next.toString()}`;
}

/** The query with the picker closed: `view` dropped, `date` KEPT so the calendar stays on that week. */
export function withoutPicker(q: Record<string, string>): string {
  const next = new URLSearchParams(q);
  next.delete("view");
  const s = next.toString();
  return s === "" ? "" : `?${s}`;
}
